import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Typography,
  stepConnectorClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

import FormDynamicElement from './FormDynamicElement';
import Successpopup from '../../SharedComponent/Successpopup';
import Brokrageaccoun from '../../Popups/Brokrageaccoun';
import Confirmpopup from '../../SharedComponent/Confirmpopup';

import { FieldType, FormField, FormSection } from './utils/formFieldTypes';
import {
  validateFormStep,
  getFieldsToValidate,
} from './utils';
import {
  getRenderableFields,
} from './utils';
import {
  prepareSubmissionPayload,
  prepareFormDataPayload,
  setPrefilledValues,
  getDisplayableFields,
} from './utils/formDataHandler';
import { autoCompleteAllAddressFields } from './utils/addressAutoComplete';

import { UrlConstant } from '../../../../commom/UrlConstant';
import Axios from '../../../../config/axios';
import { getUserFromLocalStorage } from '../../../../utils/utils';
import { KYC_STATUS, KycStatusValue } from '../../SharedComponent/OnboardingStatusBar';
import { trackEvent } from '../../../../config/firebase';
import { Constant } from '../../../../commom/Constants';

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #4EDF6D 0%, #4EDF6D 50%, #4EDF6D 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #4EDF6D 0%, #4EDF6D 50%, #4EDF6D 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 8,
    border: 0,
    backgroundColor: '#FFFFFF1A',
    borderRadius: 1,
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

interface PopupState {
  show: boolean;
  heading: string;
  message: string;
  success: boolean;
}

interface KYCStatusResponse {
  status: KycStatusValue;
  message: string;
  canCreateAccount: boolean;
  canUpdateDetails: boolean;
  currentStep: number | null;
  completedSections: string[];
  isFullyCompleted: boolean;
  isLinkBankAvailable?: boolean;
  isAus?: boolean;
}

const CreateApplication: React.FC = () => {
  // Navigation & Route
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUserFromLocalStorage();

  // Form State
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState<FormSection[]>([]);
  const [allStepsData, setAllStepsData] = useState<FormField[]>([]);
  const [currentStepData, setCurrentStepData] = useState<FormField[]>([]);
  const [selectedValues, setSelectedValues] = useState<Record<number, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [kycStatus, setKycStatus] = useState<KycStatusValue | null>(null);
  const [kycMessage, setKycMessage] = useState<string>('');
  const [isLinkBankAccount, setIsLinkBankAccount] = useState<any>(false);
  const [isAus, setIsAus] = useState<any>(false);

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [popup, setPopup] = useState<PopupState>({
    show: false,
    heading: '',
    message: '',
    success: false,
  });
  const [showBrokragePopup, setShowBrokragePopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showEConsentPopup, setShowEConsentPopup] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const showPopup = useCallback(
    (heading: string, message: string, success: boolean = false) => {
      setPopup({ show: true, heading, message, success });
    },
    []
  );

  const closePopup = useCallback(() => {
    setPopup({ show: false, heading: '', message: '', success: false });
  }, []);

  const handleAddressSelected = useCallback(
    (placeResult: google.maps.places.PlaceResult, systemIdentifier: string) => {
      try {
        const postalSameResidentialField = currentStepData.find(
          (f) => f.system_identifire === 'postalSameAsResidential'
        );
        const postalSameAsResidential =
          selectedValues[postalSameResidentialField?.id || 0] === 'true' ||
          selectedValues[postalSameResidentialField?.id || 0] === true;

        const updatedValues = autoCompleteAllAddressFields(
          placeResult,
          currentStepData,
          selectedValues,
          postalSameAsResidential,
          systemIdentifier
        );

        setSelectedValues(updatedValues);

        setCurrentStepData((prev) =>
          prev.map((field) => ({
            ...field,
            value: updatedValues[field.id] !== undefined ? updatedValues[field.id] : field.value,
          }))
        );
      } catch (error) {
        showPopup('Error', 'Failed to auto-complete address. Please fill manually.', false);
      }
    },
    [currentStepData, selectedValues, showPopup]
  );

  // Check KYC status on component mount
  const checkKYCStatus = useCallback(async (): Promise<KYCStatusResponse | null> => {
    try {
      setIsLoading(true);
      const response = await Axios.get(UrlConstant.Sumsub.CheckAccountStatus);

      if (response.data?.success) {
        const statusData = response.data.data as KYCStatusResponse;
        return statusData;
      }

      return null;
    } catch (error: any) {
      console.error('Error checking KYC status:', error);
      showPopup('Error', 'Failed to check account status. Please try again.', false);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [showPopup]);

  // Handle different KYC statuses and show appropriate UI
  const handleKYCStatusCheck = useCallback(async (statusData: KYCStatusResponse) => {
    const { status, message, canCreateAccount, isFullyCompleted, currentStep, isLinkBankAvailable, isAus } = statusData;

    setKycStatus(status);
    setKycMessage(message);
    setIsLinkBankAccount(isLinkBankAvailable);
    setIsAus(isAus);

    // If fully completed - show success popup
    if (isFullyCompleted) {
      setShowBrokragePopup(true);
      return false; // Don't proceed to form
    }

    // If user can't create/update account - show status popup
    if (!canCreateAccount) {
      setShowBrokragePopup(true);
      return false; // Don't proceed to form
    }

    // User can proceed with form - return the step they should start from
    return {
      shouldProceed: true,
      startFromStep: currentStep ?? 0,
    };
  }, []);

  const getAccountForm = useCallback(async (startFromStep: number = 0) => {
    setIsLoading(true);
    try {
      const response = await Axios.post(UrlConstant.Sumsub.CreateAccount, {});
      const resData = response.data;

      if (resData.status === "success") {
        const data = resData.data as FormField[];
        const filteredData = data
          .sort((a, b) => a.position - b.position)
          .filter((e) => e.is_display_section === 1);

        setAllStepsData(filteredData);

        const stepsArray = filteredData.map((section) => ({
          section_type: section.section_type,
          section_name: section.section_name || '',
          section_info: section.section_info || '',
          position: section.position,
          is_display_section: section.is_display_section,
          questions: getDisplayableFields(section.questions || []),
        }));

        setSteps(stepsArray);

        // Set active step based on backend response or provided step
        const targetStep = Math.min(startFromStep, filteredData.length - 1);
        setActiveStep(targetStep);

        // Initialize the appropriate step
        if (filteredData[targetStep]) {
          await initializeStep(filteredData[targetStep]);
        }
      }
    } catch (error) {
      showPopup('Error', 'Failed to load form. Please try again.', false);
    } finally {
      setIsLoading(false);
    }
  }, [showPopup]);

  const getSectionAnswers = useCallback(
    async (sectionType: string): Promise<{ answers: any[] }> => {
      setIsLoading(true);

      try {
        const { data } = await Axios.post(UrlConstant.Sumsub.GetSectionAnswer, {
          section_type: sectionType,
        });

        const answers = data?.data?.data ?? [];
        return { answers };
      } catch (error: any) {
        return { answers: [] };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const setAnswerAndQuestion = useCallback(
    async (currentStep: FormField, answerList: any[]) => {
      const displayableQuestions = getDisplayableFields(currentStep.questions || []);
      const prefilledValues = setPrefilledValues(displayableQuestions, answerList);

      // Check isPhoneVerified from answer data
      displayableQuestions.forEach((field) => {
        if (field.type === FieldType.PHONE_OTP) {
          const mobileNumberAnswer = answerList.find(
            (item) => item.system_identifire === 'mobileNumber'
          );

          if (mobileNumberAnswer?.isPhoneVerified) {
            prefilledValues[`${field.id}_verified`] = true;
          }
        }

        // Check for file upload fields with existing data
        if (field.type === FieldType.FILE_UPLOAD) {
          const fileAnswer = answerList.find(
            (item) => item.system_identifire === field.system_identifire
          );

          if (fileAnswer?.answer?.previewUrl) {
            // Store preview URL for display
            prefilledValues[`${field.id}_preview`] = fileAnswer.answer.previewUrl;
          }
        }
      });

      const stepWithAnswers = displayableQuestions.map((field) => ({
        ...field,
        value: prefilledValues[field.id] || '',
        dynamicHint: answerList.find((item) => item.question_id === field.id)?.dynamic_hint, 
      }));

      setCurrentStepData(stepWithAnswers);
      setSelectedValues(prefilledValues);
    },
    []
  );

  const initializeStep = useCallback(async (step: FormField) => {
    setCurrentStepData(getDisplayableFields(step.questions || []));
    const { answers } = await getSectionAnswers(step.section_type);
    await setAnswerAndQuestion(step, answers);
  }, [getSectionAnswers, setAnswerAndQuestion]);

  const handleInputChange = useCallback((field: FormField) => {
    setSelectedValues((prev) => ({
      ...prev,
      [field.id]: field.value,
    }));
    setValidationErrors((prev) => {
      if (!(field.id in prev)) return prev;
      const next = { ...prev };
      delete next[field.id];
      return next;
    });

    setCurrentStepData((prev) =>
      prev.map((f) => (f.id === field.id ? { ...f, value: field.value } : f))
    );
  }, []);

  const hasFileUploads = useCallback((fields: FormField[]): boolean => {
    return fields.some(field => field.type === FieldType.FILE_UPLOAD);
  }, []);

  const submitAnswer = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const visibleFields = getRenderableFields(currentStepData, selectedValues);

      const needsFormData = hasFileUploads(visibleFields);

      let submitData: any;
      let contentType: string;

      if (needsFormData) {
        submitData = prepareFormDataPayload(
          visibleFields,
          selectedValues,
          currentStepData[0].section_type
        );
        contentType = 'multipart/form-data';
      } else {
        submitData = prepareSubmissionPayload(
          visibleFields,
          selectedValues,
          currentStepData[0].section_type
        );
        contentType = 'application/json';
      }

      await Axios.post(UrlConstant.Sumsub.SubmitAnswer, submitData, {
        headers: {
          'Content-Type': contentType,
        },
      });

      return true;
    } catch (error: any) {
      const responseData = error?.response?.data;

      const isValidationError =
        responseData?.message?.[0] === 'Validation failed' &&
        typeof responseData?.errors === 'object' &&
        !Array.isArray(responseData.errors);

      if (isValidationError) {
        // ✅ Only set inline errors, NO popup
        setValidationErrors(responseData.errors);
        return false;
      }

      // ❌ Only show popup for non-validation errors
      showPopup(
        'Error',
        responseData?.message?.[0] ?? 'Something went wrong',
        false
      );

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentStepData, selectedValues, hasFileUploads, showPopup]);

  const submitForm = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await Axios.get(UrlConstant.Sumsub.SubmitForm);
      const resData = response.data;

      if (resData.status === "success") {
        const kycData = resData.data;
        if (Constant.ENV === "production") {
          trackEvent("trading_account_completed", {
            source: `KYC_STATUS: ${kycData?.status || "unknown"}`,
          })
        }
        
        // If action_required, redirect to form with specific step
        if (kycData?.status === KYC_STATUS.ACTION_REQUIRED && kycData?.currentStep !== undefined) {
          setIsLoading(false);
          navigate('/createApplication', {
            state: {
              startStep: kycData.currentStep,
              message: kycData.message,
              errorDescription: kycData.errorDescription,
            },
          });
          return;
        }
        
        setKycStatus(kycData?.status || KYC_STATUS.PENDING);
        setKycMessage(kycData?.message || '');
        setIsLinkBankAccount(kycData?.isLinkBankAvailable ?? false);
        setIsAus(kycData?.isAus ?? false);
        setShowBrokragePopup(true);
      } else {
        showPopup(
          'Submission Failed',
          resData?.message || 'Failed to submit application. Please try again.',
          false
        );
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message
        || error?.message
        || 'Something went wrong while submitting your application. Please try again.';

      showPopup('Error', errorMessage, false);
    } finally {
      setIsLoading(false);
    }
  }, [showPopup, navigate]);

  const handleNext = useCallback(async () => {
    // if (currentStepData[0]?.section_type === 'personal_details' && !proceedAfterConfirm) {
    //   setProceedAfterConfirm(true);
    //   setShowConfirmPopup(true);
    //   return;
    // }

    const fieldsToValidate = getFieldsToValidate(currentStepData, selectedValues);
    const errors = validateFormStep(fieldsToValidate, selectedValues);

    // Check phone_otp fields are verified before proceeding
    currentStepData.forEach((field) => {
      if (field.type === FieldType.PHONE_OTP && !field.isOptional) {
        const isVerified = selectedValues[`${field.id}_verified`];
        if (!isVerified) {
          errors[field.id] = 'Please verify your phone number before proceeding.';
        }
      }
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Submit answer
    const isSubmitted = await submitAnswer();
    if (!isSubmitted) {
      return;
    }

    // Refresh form after country_details is submitted to get updated contact_details and conditional fields
    if (currentStepData[0]?.section_type === 'country_details') {
      await getAccountForm(activeStep + 1);
      return;
    }

    // Determine next action
    const nextStepAtIndex = activeStep + 1;
    const nextSection = allStepsData[nextStepAtIndex];

    if (!nextSection) {
      // No more steps - submit form
      await submitForm();
    } else {
      // Move to next step
      setActiveStep(nextStepAtIndex);
      const { answers } = await getSectionAnswers(nextSection.section_type);
      await setAnswerAndQuestion(nextSection, answers);
      setValidationErrors({});
    }
  }, [
    currentStepData,
    selectedValues,
    activeStep,
    allStepsData,
    submitAnswer,
    submitForm,
    getSectionAnswers,
    setAnswerAndQuestion,
    getAccountForm,
  ]);

  const handleBack = useCallback(async () => {
    if (activeStep === 0) return;

    const prevSection = allStepsData[activeStep - 1];
    if (!prevSection) return;

    setActiveStep(activeStep - 1);
    const { answers } = await getSectionAnswers(prevSection.section_type);
    await setAnswerAndQuestion(prevSection, answers);
    setValidationErrors({});
  }, [activeStep, allStepsData, getSectionAnswers, setAnswerAndQuestion]);

  // Initialize on mount - check status first, then load form if appropriate
  useEffect(() => {
    const initialize = async () => {
      // Check if redirected with a specific step
      const locationState = location.state as any;
      const redirectedStep = locationState?.startStep;
      const redirectedMessage = locationState?.message;

      // If redirected with a step, load form from that step
      if (redirectedStep !== undefined) {
        if (redirectedMessage) {
          setKycMessage(redirectedMessage);
          showPopup('Action Required', (<>{redirectedMessage}{locationState?.errorDescription && <><br /><br />{locationState.errorDescription}</>}</>) as any, false);   
        }
        await getAccountForm(redirectedStep);
        return;
      }

      // Check e-consent first
      if (!user?.econsentAccepted) {
        setShowEConsentPopup(true);
        return;
      }

      // Check KYC status
      const statusData = await checkKYCStatus();

      if (!statusData) {
        // If status check failed, still try to load form
        await getAccountForm(0);
        return;
      }

      // Handle the status and determine if/where to proceed
      const result = await handleKYCStatusCheck(statusData);
      if (result && result.shouldProceed) {
        // Load form starting from the appropriate step
        await getAccountForm(result.startFromStep);
      }
      // Otherwise status popup is shown, don't load form
    };

    initialize();
  }, [user?.econsentAccepted, location.state]);

  const visibleFields = getRenderableFields(currentStepData, selectedValues);
  const currentSectionName = steps[activeStep]?.section_name || '';
  const isLastStep = activeStep === steps.length - 1;
  const isFirstStep = activeStep === 0;

  const isNextDisabled = useMemo(() => {
    if (isLoading) return true;

    // Disable if any required phone_otp field is not verified
    const hasUnverifiedPhone = currentStepData.some(
      (field) =>
        field.type === FieldType.PHONE_OTP &&
        !field.isOptional &&
        !selectedValues[`${field.id}_verified`]
    );

    return hasUnverifiedPhone;
  }, [isLoading, currentStepData, selectedValues]);

  return (
    <Container
      maxWidth={false}
      sx={{
        height: 'calc(100vh - 101px)',
        paddingLeft: '0px',
        paddingRight: '0px',
      }}
      className='bg-color'
    >
      <Box height='100%' p='30px' sx={{ width: '100%' }} className='bg-color'>
        {/* Stepper */}
        {steps.length > 1 && (
          <Stepper
            sx={{
              height: '5%',
              mb: '30px',
              '.MuiStepIcon-root': {
                height: '42px',
                width: '42px',
              },
              '.MuiStep-root': {
                padding: '0px',
              },
              '.MuiStepLabel-iconContainer': {
                paddingRight: '0px',
              },
              '.MuiStepIcon-text': { fontSize: '16px' },
            }}
            activeStep={activeStep}
            connector={<CustomConnector />}
          >
            {steps.map((step, index) => (
              <Step key={`${step.section_type}-${index}`}>
                <StepLabel />
              </Step>
            ))}
          </Stepper>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
            }}
          >
            <CircularProgress size='40px' color='primary' />
          </Box>
        )}

        {/* Form Content */}
        {!isLoading && steps.length > 0 && (
          <Box height='90%' position='relative' display='flex' flexDirection='column' justifyContent='space-between'>
            {activeStep === steps.length ? (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography>All steps completed - you're finished</Typography>
              </Box>
            ) : (
              <>
                {/* Fields Rendering */}
                <Box width='100%' height='80%' sx={{ overflowY: 'auto', pr: '10px' }} ref={scrollRef}>
                  <Typography sx={{ color: 'white', fontSize: '16px', mb: '20px', fontWeight: 'bold' }}>
                    {currentSectionName}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'flex-start',
                      width: '100%',
                      flexWrap: 'wrap',
                    }}
                  >
                    {visibleFields.map((field) => (
                      <FormDynamicElement
                        key={field.id}
                        elementDetail={field}
                        currentStepData={currentStepData}
                        selectedValues={selectedValues}
                        validationErrors={validationErrors}
                        onChange={handleInputChange}
                        setSelectedValues={setSelectedValues}
                        onAddressSelected={handleAddressSelected}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Navigation Buttons */}
                <Box position='absolute' bottom='0px' width='100%'>
                  <Divider
                    sx={{
                      borderColor: '#b3b3b3',
                      mb: '20px',
                      borderWidth: 1,
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      {!isFirstStep && (
                        <Button
                          onClick={handleBack}
                          sx={{
                            color: 'white',
                            bgcolor: '#FFFFFF33',
                            fontSize: '16px',
                            borderRadius: '50px',
                            fontWeight: 'bold',
                            padding: '13px 80px',
                          }}
                        >
                          Back
                        </Button>
                      )}
                    </Box>

                    <Box>
                      <Button
                        sx={{
                          color: 'white',
                          bgcolor: '#f2674a',
                          fontSize: '16px',
                          borderRadius: '50px',
                          fontWeight: 'bold',
                          padding: '13px 80px',
                          '&:hover': {
                            bgcolor: '#ff5c3d',
                          },
                          '&:disabled': {
                            bgcolor: '#666',
                            color: '#999',
                          },
                        }}
                        onClick={handleNext}
                        disabled={isNextDisabled}
                      >
                        {isLastStep ? 'Finish' : 'Next'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Popups */}
      {popup.show && (
        <Successpopup
          Heading={popup.heading}
          Success={popup.success}
          onClose={closePopup}
          Message={popup.message}
        />
      )}

      <Confirmpopup
        open={showConfirmPopup}
        message='Please ensure that you have provided a valid email address and mobile number, as they will be verified in the next steps.'
        onClose={() => setShowConfirmPopup(false)}
        onConfirm={async () => {
          setShowConfirmPopup(false);
          await handleNext();
        }}
        confirmText='Continue'
      />

      {showBrokragePopup && (
        <Brokrageaccoun
          onClose={() => {
            setShowBrokragePopup(false);
            // Navigate based on status
            if (kycStatus === KYC_STATUS.ACTION_REQUIRED) {
              // Stay on page for action required - user can update details
              return;
            }
            navigate('/dashboard');
          }}
          kycStatus={kycStatus || undefined}
          message={kycMessage}
          isLoading={isLoading}
          setShowBrokragePopup={setShowBrokragePopup}
          isLinkBankAvailable={isLinkBankAccount}
          isAus={isAus}
        />
      )}

      {showEConsentPopup && (
        <Confirmpopup
          open={showEConsentPopup}
          onClose={() => setShowEConsentPopup(false)}
          onConfirm={() => {
            setShowEConsentPopup(false);
            navigate('/e-consent');
          }}
          message='You need to first accept e-consent for onboarding your account. After that, you will be able to proceed.'
        />
      )}
    </Container>
  );
};

export default CreateApplication;